import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Select,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomToast, FormInput } from "../../components";
import { MultiSelectMenu } from "../../components/MultiSelectMenu/MultiSelectMenu";
import { useFetch } from "../../hooks/useFetch";
import { refreshHistory } from "../../components/History/modal";

interface FollowUpEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number;
  followUpId: number;
  onFollowUpSaved?: () => void;
}

interface IFollowUpForm {
  nextFollowupDate: string;
  followTime: string;
  description: string;
  remarks?: string;
  status: string;
}

const defaultValues: IFollowUpForm = {
  nextFollowupDate: "",
  followTime: "",
  description: "",
  remarks: "",
  status: "",
};

export enum MessageType {
  Email = 1,
  SMS = 2,
  Whatsapp = 3,
  Call = 4,
}

const messageTypeOptions = [
  { id: MessageType.Email, name: "Email" },
  { id: MessageType.SMS, name: "SMS" },
  { id: MessageType.Whatsapp, name: "Whatsapp" },
  { id: MessageType.Call, name: "Call" },
];

const schema = yup.object({
  nextFollowupDate: yup.string().required("Follow up date is required"),
  followTime: yup.string().required("Follow up time is required"),
  remarks: yup.string().optional(),
  description: yup.string().required("Description is required"),
});

const FollowUpEditModal = ({
  isOpen,
  onClose,
  leadId,
  followUpId,
  onFollowUpSaved,
}: FollowUpEditModalProps) => {
  const { addToast } = CustomToast();
  const { fetchApi, loading: apiLoading } = useFetch(addToast);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [selectedMessageTypes, setSelectedMessageTypes] = useState<number[]>([]);
  const [showRemarks, setShowRemarks] = useState(false);
  const [isCancelledRecord, setIsCancelledRecord] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors },
  } = useForm<IFollowUpForm>({
    resolver: yupResolver(schema) as any,
    defaultValues,
  });

  const loadFollowUpDetails = async () => {
    try {
      setLoadingDetails(true);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await fetchApi(
        `LeadRequirement/LeadFollowUp?leadId=${leadId}&filter=all&status=all&timeZone=${encodeURIComponent(
          timezone
        )}`,
        "GET"
      );

      if (Array.isArray(res?.data)) {
        const match = res.data.find((f: any) => f.id === followUpId);
        if (match) {
          reset({
            nextFollowupDate: match.nextFollowupDate?.split("T")[0] || "",
            followTime: match.followTime
              ? new Date(
                  match.followTime.endsWith("Z")
                    ? match.followTime
                    : `${match.followTime}Z`
                ).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
              : "",
            description: match.description || "",
            remarks: match.remarks || "",
            status: match.followUp_Status || "",
          });
          setSelectedMessageTypes(
            match.messageTypes?.map((x: any) => x.id) || []
          );
          setShowRemarks(match.isCompleted || false);
          setIsCancelledRecord(match.followUp_Status === "Cancelled");
        } else {
          addToast({
            message: "Follow-up details not found.",
            status: "error",
          });
          onClose();
        }
      } else {
        addToast({
          message: "Failed to load follow-up details.",
          status: "error",
        });
        onClose();
      }
    } catch (err) {
      console.error(err);
      onClose();
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen && leadId && followUpId) {
      loadFollowUpDetails();
    }
  }, [isOpen, leadId, followUpId]);

  const onSubmit = async (data: IFollowUpForm) => {
    if (selectedMessageTypes.length === 0) {
      addToast({
        message: "Please select at least one message type",
        status: "warning",
      });
      return;
    }

    const followTimeUtc =
      data.followTime && data.nextFollowupDate
        ? new Date(`${data.nextFollowupDate}T${data.followTime}:00`).toISOString()
        : null;

    const payload = {
      leadId,
      nextFollowupDate: data.nextFollowupDate,
      followTime: followTimeUtc,
      description: data.description,
      messageType: selectedMessageTypes,
      isCompleted: showRemarks,
      followUp_Status: data.status,
      remarks: data.remarks,
    };

    const res = await fetchApi(
      `LeadRequirement/LeadFollowUp/${followUpId}`,
      "PUT",
      payload,
      null,
      "Follow Up Updated Successfully"
    );

    if (res) {
      if (onFollowUpSaved) onFollowUpSaved();
      refreshHistory("Lead", leadId);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>Edit Follow Up</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingDetails ? (
              <Flex justify="center" py={10}>
                <Spinner thickness="2px" speed="0.65s" color="#4048D6" size="lg" />
              </Flex>
            ) : (
              <Flex direction="column" gap={3}>
                <Flex gap={2}>
                  <FormInput
                    isRequired
                    name="nextFollowupDate"
                    control={control}
                    label="Follow Up Date"
                    type="date"
                    errors={errors}
                    min={today}
                    disable={isCancelledRecord}
                  />
                  <FormInput
                    isRequired
                    name="followTime"
                    control={control}
                    label="Follow Up Time"
                    type="time"
                    errors={errors}
                    disable={isCancelledRecord}
                  />
                </Flex>
                <FormControl isRequired isDisabled={isCancelledRecord}>
                  <FormLabel className="font-poppins text_medium text_md">
                    Follow-Up Channel
                  </FormLabel>
                  <MultiSelectMenu
                    options={messageTypeOptions}
                    labelKey="name"
                    valueKey="id"
                    selectedValues={selectedMessageTypes}
                    setSelectedValues={setSelectedMessageTypes}
                    placeholder="Select Message Types"
                    isDisabled={isCancelledRecord}
                  />
                </FormControl>

                {showRemarks && (
                  <GridItem colSpan={2}>
                    <FormControl isDisabled={isCancelledRecord}>
                      <FormLabel className="font-poppins text_medium text_md">
                        Remarks
                      </FormLabel>
                      <Textarea
                        placeholder="Add Remarks"
                        bg="white"
                        className="font_poppins text_regular text_sm"
                        {...register("remarks")}
                      />
                    </FormControl>
                  </GridItem>
                )}

                <FormControl isDisabled={isCancelledRecord}>
                  <FormLabel className="font-poppins text_medium text_md">
                    Status
                  </FormLabel>
                  <Select {...register("status")}>
                    <option value="">Select Status</option>
                    <option value="Done">Done</option>
                    <option value="Pending">Pending</option>
                    <option value="Cancelled">Cancelled</option>
                  </Select>
                </FormControl>

                <FormControl isInvalid={!!errors.description} isDisabled={isCancelledRecord}>
                  <FormLabel className="font-poppins text_medium text_md">
                    Description
                  </FormLabel>
                  <Textarea
                    placeholder="Add Description"
                    bg="white"
                    className="font_poppins text_regular text_sm"
                    {...register("description")}
                  />
                  {errors.description && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.description.message}
                    </Text>
                  )}
                </FormControl>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#4048D6"
              color="white"
              type="submit"
              isLoading={apiLoading}
              isDisabled={isCancelledRecord || loadingDetails}
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default FollowUpEditModal;
